import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form-mui';
import { APIContext } from '../../../../Contexts/APIContext';
import DataContainer from '../../../../Utilities/DataContainer';
import RenderSelectField from '../../../../Utilities/Forms/RenderSelectField';
import RenderTextField from '../../../../Utilities/Forms/RenderTextField';
import ValidatedSelectElement from '../../../../Utilities/Forms/ValidatedSelectElement';
import ValidatedTextField from '../../../../Utilities/Forms/ValidatedTextField';
import monthDropdown from '../../../../Utilities/Constants/MonthDropdown';
import ShiftPatternDropdown from '../../../../Utilities/Constants/ShifttPatternDropdown';
import DropDownViewModel from '../../../../Utilities/ViewModels/DropDownViewModel';
import yesNoDropdown from '../../../../Utilities/Constants/YesNoDropdown';
import CrabPie from '../../../../Utilities/CrabPie';
import DftConnectivityToolScores from '../../../DftConnectivityToolScores';
import SiteViewModel from '../../../ViewModels/SiteViewModel';
interface IProps {
    planTypes: DropDownViewModel[]
    targetGroups: DropDownViewModel[]
    id: string
    isReadOnly: boolean
    site: SiteViewModel
}

export default function TravelPlanMainDetailsV1({ planTypes, targetGroups, id, isReadOnly, site }: IProps) {

    const { getValues } = useFormContext();
    const formValues = getValues();
    const hasValues = (fields: string[]) => fields.some(field => formValues[field]) || !isReadOnly;
    const { get } = useContext(APIContext);

    const [prePlanChart, setPrePlanSvgUrl] = useState<PieSegment[]>([]);
    const [modalChart, setModalSvgUrl] = useState<PieSegment[]>([]);

    React.useEffect(() => {
        getTravelPlanChart();
    }, []);

    const getTravelPlanChart = () => {
        get('TravelPlan', `GetPrePlanModeSplitChart?id=${id}`, setPrePlanSvgUrl)
        get('TravelPlan', `GetModalSplitChart?id=${id}`, setModalSvgUrl)
    };

    return (
        <>
            <Grid container alignItems="flex-start">
                <Grid size={4} container>
                    {hasValues(["implementationMonth", "implementationYear", "accreditedOrRewardedForQuality"]) && (
                        <Grid container size={12}>
                            <DataContainer title={"Travel Plan History"}>
                                <Grid container size={12}>
                                    <Grid size={12}>
                                        <Typography variant={"body1"}>Date of travel plan implementation</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'implementationMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'implementationYear'} />
                                        </Grid>
                                    </Stack>
                                </Grid>

                                <RenderSelectField label="Has the Travel Plan been accredited, or received an award recognition of its quality, from either a national body such as ACT or a local authority" name="accreditedOrRewardedForQuality" options={yesNoDropdown} readOnly={isReadOnly} />
                            </DataContainer>
                        </Grid>
                    )}

                    {hasValues(["travelPlanCoordinator", "coordinatorShiftType"]) && (
                        <Grid container size={12}>
                            <DataContainer title={"Pedestrians"}>
                                <Stack spacing={1}>
                                    <RenderSelectField label="Is there a Travel Plan co-ordinator - a member of staff whose job it is to manage the implementation of the Travel Plan" name="travelPlanCoordinator" options={yesNoDropdown} readOnly={isReadOnly} />
                                    <RenderSelectField label="If YES to the above, do they work on the Travel Plan full time or part time?" name="coordinatorShiftType" options={ShiftPatternDropdown} readOnly={isReadOnly} />
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}

                    {hasValues(["type", "typeOtherSpecify"]) && (
                        <Grid container size={12}>
                            <Grid size={12}>
                                <DataContainer title={"Travel Plan Type"}>
                                    <Stack spacing={1}>
                                        <RenderSelectField label="Type" name="type" options={planTypes} readOnly={isReadOnly} labelSize={4} inputSize={8} />
                                        {formValues.type == "O" &&
                                            <RenderTextField label="Other Specified" name="typeOtherSpecify" readOnly={isReadOnly} labelSize={12} inputSize={12} multiLine={true} rows={5} />
                                        }
                                    </Stack>
                                </DataContainer>
                            </Grid>
                        </Grid>
                    )}

                    {hasValues(["targetGroupStaff", "targetGroupVisitors", "targetGroupCustomers", "targetGroupStudents", "targetGroupPatients", "targetGroupAllSiteUsers", "targetGroupOther", "targetGroupOtherSpecify"]) && (
                        <Grid container size={12}>
                            <Grid size={12}>
                                <DataContainer title={"Travel Plan Target Group"}>
                                    <Stack spacing={1}>
                                        <Grid size={12}>
                                            <Typography variant={"body1"}>Main target markets for the Travel Plan</Typography>
                                        </Grid>
                                        <RenderSelectField label="Staff" name="targetGroupStaff" options={targetGroups} readOnly={isReadOnly} labelSize={4} inputSize={8} />
                                        <RenderSelectField label="Visitors" name="targetGroupVisitors" options={targetGroups} readOnly={isReadOnly} labelSize={4} inputSize={8} />
                                        <RenderSelectField label="Customers" name="targetGroupCustomers" options={targetGroups} readOnly={isReadOnly} labelSize={4} inputSize={8} />
                                        <RenderSelectField label="Students" name="targetGroupStudents" options={targetGroups} readOnly={isReadOnly} labelSize={4} inputSize={8} />
                                        <RenderSelectField label="Patients" name="targetGroupPatients" options={targetGroups} readOnly={isReadOnly} labelSize={4} inputSize={8} />
                                        <RenderSelectField label="All Site Users" name="targetGroupAllSiteUsers" options={targetGroups} readOnly={isReadOnly} labelSize={4} inputSize={8} />
                                        <RenderSelectField label="Other" name="targetGroupOther" options={targetGroups} readOnly={isReadOnly} labelSize={4} inputSize={8} />
                                        <RenderTextField label="Other Specified" name="targetGroupOtherSpecify" readOnly={isReadOnly} labelSize={12} inputSize={12} />
                                    </Stack>
                                </DataContainer>
                            </Grid>
                        </Grid>

                    )}
                </Grid>

                <Grid size={4} container>
                    {hasValues(["prePlanModalSplit", "modalSplitMonth", "percentageVehicles", "percentageCyclists", "percentagePublicTransport", "percentagePublicPedestrians"]) && (
                        <Grid container size={12}>
                            <Grid size={12}>
                                <DataContainer title={"Pre-Travel Plan Mode Split"}>
                                    <Stack spacing={1}>
                                        <RenderSelectField label="Was modal split data obtained before the introduction of the Travel Plan?" name="prePlanModalSplit" options={yesNoDropdown} readOnly={isReadOnly} />
                                        <Grid container size={12}>
                                            <Grid size={12}>
                                                <Typography variant={"body1"}>If YES, when?</Typography>
                                            </Grid>
                                            <Stack direction="row" spacing={1}>
                                                <Grid size={6}>
                                                    <ValidatedSelectElement name={'modalSplitMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                                </Grid>
                                                <Grid size={6}>
                                                    <ValidatedTextField name={'modalSplitYear'} />
                                                </Grid>
                                            </Stack>
                                        </Grid>
                                        <RenderTextField label="Vehicle Occupants" name="percentageVehicles" readOnly={isReadOnly} />
                                        <RenderTextField label="Cyclists" name="percentageCyclists" readOnly={isReadOnly} />
                                        <RenderTextField label="Public Transport Users" name="percentagePublicTransport" readOnly={isReadOnly} />
                                        <RenderTextField label="Pedestrians" name="percentagePublicPedestrians" readOnly={isReadOnly} />
                                    </Stack>
                                </DataContainer>
                            </Grid>
                            <Grid container size={12}>
                                {prePlanChart.length !== 0 && <CrabPie data={prePlanChart} />}
                            </Grid>
                        </Grid>
                    )}

                    <Grid container size={12}>
                        <Grid size={12}>
                            <DataContainer title={"Travel Plan Targets"}>
                                <RenderTextField name="travelPlanTargetComment" readOnly={isReadOnly} multiLine={true} rows={4} labelSize={12} inputSize={12} label={''} alwaysShow={true} />
                            </DataContainer>
                        </Grid>
                    </Grid>

                </Grid>

                <Grid size={4} container>
                    {hasValues(["modeSplitTargetsIdentified", "modeSplitTargetsIdentifiedMonth", "modeSplitTargetsIdentifiedYear", "modalSplitVehicles", "modalSplitCyclists", "modalSplitPublicTransport", "modalSplitPedestrians"]) && (
                        <Grid container size={12}>
                            <Grid size={12}>
                                <DataContainer title={"Travel Plan Modal Split Targets"}>
                                    <Stack spacing={1}>
                                        <RenderSelectField label="Did the Travel Plan identify mode split targets?" name="modeSplitTargetsIdentified" options={yesNoDropdown} readOnly={isReadOnly} />
                                        <Grid container size={12}>
                                            <Grid size={12}>
                                                <Typography variant={"body1"}>If YES, when?</Typography>
                                            </Grid>
                                            <Stack direction="row" spacing={1}>
                                                <Grid size={6}>
                                                    <ValidatedSelectElement name={'modeSplitTargetsIdentifiedMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                                </Grid>
                                                <Grid size={6}>
                                                    <ValidatedTextField name={'modeSplitTargetsIdentifiedYear'} />
                                                </Grid>
                                            </Stack>
                                        </Grid>
                                        <RenderTextField label="Vehicle Occupants" name="modalSplitVehicles" readOnly={isReadOnly} />
                                        <RenderTextField label="Cyclists" name="modalSplitCyclists" readOnly={isReadOnly} />
                                        <RenderTextField label="Public Transport Users" name="modalSplitPublicTransport" readOnly={isReadOnly} />
                                        <RenderTextField label="Pedestrians" name="modalSplitPedestrians" readOnly={isReadOnly} />
                                    </Stack>
                                </DataContainer>
                            </Grid>
                            <Grid container size={12}>
                                {modalChart.length !== 0 && <CrabPie data={modalChart} />}
                            </Grid>
                        </Grid>
                    )}

                    <Grid container size={12}>
                        <Grid size={12}>
                            <DataContainer title={"Additional Travel Plan Features"}>
                                <RenderTextField name="additionalFeaturesComment" readOnly={isReadOnly} multiLine={true} rows={4} labelSize={12} inputSize={12} label={''} alwaysShow={true} />
                            </DataContainer>
                        </Grid>
                    </Grid>

                    <Grid container size={12}>
                        <Grid size={12}>
                            <DataContainer title={"Changes from Previous Survey"}>
                                <RenderTextField name="changesFromPreviousSurveysComment" readOnly={isReadOnly} multiLine={true} rows={4} labelSize={12} inputSize={12} label={''} alwaysShow={true} />
                            </DataContainer>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid >
            <DftConnectivityToolScores
                score={site.dftConnectivityToolScore}
                eligible={site.isDftConnectivityToolEligible}
                enabled={site.isDftConnectivityToolEnabled}
                readOnly={true}
                travelPlanLayout
            />
        </>
    );
}
